import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import { useMaximize } from "./hooks/useMaximize";

export default function MySalesReport({ user, storeSettings, onClose }) {
    const { overlayClass, panelClass, toggle, icon } = useMaximize(true);

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const storeName = storeSettings?.store_name ?? "Guadaye Mini Mart";
    const storeTIN = storeSettings?.store_tin ?? "";
    const storeAddress = storeSettings?.store_address ?? "";
    const storePhone = storeSettings?.store_phone ?? "";
    const vatEnabled = storeSettings?.vat_enabled === "true";

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchMyTransactions();
    }, []);

    async function fetchMyTransactions() {
        setLoading(true);
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        const { data, error } = await supabase
            .from("sales_transactions")
            .select(`
        id, receipt_number, created_at,
        subtotal, tax_amount, total_amount,
        payment_method, amount_tendered, change_amount,
        status,
        sale_items (
          product_name, quantity, unit_price, line_total
        )
      `)
            .eq("cashier_id", user.id)
            .eq("status", "completed")
            .gte("created_at", start.toISOString())
            .lte("created_at", end.toISOString())
            .order("created_at", { ascending: false });

        logSupabaseError("MySalesReport: fetch my sales_transactions", error);
        if (!error && data) setTransactions(data);
        setLoading(false);
    }

    function handlePrintReceipt(tx) {
        const win = window.open("", "_blank", "width=320,height=600");
        if (!win) return;
        const items = tx.sale_items ?? [];
        win.document.write(`
      <html><head><title>Receipt</title>
      <style>
        body { font-family: monospace; font-size: 12px; padding: 16px; max-width: 280px; margin: 0 auto; }
        .center { text-align: center; }
        .bold   { font-weight: bold; }
        .line   { border-top: 1px dashed #000; margin: 8px 0; }
        .row    { display: flex; justify-content: space-between; margin: 2px 0; }
      </style></head><body>
      <div class="center bold" style="font-size:14px">${storeName}</div>
      ${storeAddress ? `<div class="center">${storeAddress}</div>` : ""}
      ${storePhone ? `<div class="center">Tel: ${storePhone}</div>` : ""}
      ${storeTIN ? `<div class="center">TIN: ${storeTIN}</div>` : ""}
      <div class="line"></div>
      <div class="row"><span>Receipt:</span><span>${tx.receipt_number}</span></div>
      <div class="row"><span>Date:</span><span>${new Date(tx.created_at).toLocaleString()}</span></div>
      <div class="line"></div>
      ${items.map(i => `
        <div>${i.product_name}</div>
        <div class="row">
          <span>${i.quantity} × ETB ${Number(i.unit_price).toFixed(2)}</span>
          <span>ETB ${Number(i.line_total).toFixed(2)}</span>
        </div>
      `).join("")}
      <div class="line"></div>
      <div class="row"><span>Subtotal</span><span>ETB ${Number(tx.subtotal).toFixed(2)}</span></div>
      ${vatEnabled && tx.tax_amount > 0
                ? `<div class="row"><span>VAT</span><span>ETB ${Number(tx.tax_amount).toFixed(2)}</span></div>`
                : ""}
      <div class="row bold"><span>TOTAL</span><span>ETB ${Number(tx.total_amount).toFixed(2)}</span></div>
      ${tx.payment_method === "cash" ? `
        <div class="row"><span>Tendered</span><span>ETB ${Number(tx.amount_tendered).toFixed(2)}</span></div>
        <div class="row"><span>Change</span><span>ETB ${Number(tx.change_amount).toFixed(2)}</span></div>
      ` : `<div class="row"><span>Payment</span><span style="text-transform:capitalize">${tx.payment_method}</span></div>`}
      <div class="line"></div>
      <div class="center">Thank you for shopping!</div>
      </body></html>
    `);
        win.document.close();
        win.print();
    }

    const totalRevenue = transactions.reduce((s, t) => s + Number(t.total_amount), 0);
    const totalTransactions = transactions.length;
    const avgBasket = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

    const paymentBreakdown = transactions.reduce((acc, t) => {
        acc[t.payment_method] = (acc[t.payment_method] ?? 0) + Number(t.total_amount);
        return acc;
    }, {});

    return (
        <div className={overlayClass}>
            <div className={panelClass} style={{ maxWidth: "980px" }}>

                {/* Header */}
                <div className="report-header">
                    <div>
                        <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
                            🧾 My Sales Today
                        </h2>
                        <p style={{ margin: "2px 0 0", fontSize: "12px", color: "var(--text3)" }}>
                            {new Date().toLocaleDateString("en-GB", {
                                weekday: "long", year: "numeric",
                                month: "long", day: "numeric"
                            })}
                        </p>
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button className="maximize-btn" onClick={toggle} title={icon === "⊞" ? "Maximize" : "Restore"}>
                            {icon}
                        </button>
                        <button onClick={fetchMyTransactions} className="report-close" title="Refresh">
                            ↺
                        </button>
                    </div>
                    <button onClick={onClose} className="report-close">✕</button>
                </div>

                {loading ? (
                    <div style={{ padding: "40px", textAlign: "center", color: "var(--text3)" }}>
                        Loading your sales...
                    </div>
                ) : (
                    <div style={{ padding: "16px", overflowY: "auto", flex: 1 }}>

                        {/* KPIs */}
                        <div className="mysales-kpi-row" style={{ marginBottom: "20px" }}>
                            {[
                                { label: "Total Revenue", value: `ETB ${totalRevenue.toFixed(2)}`, color: "var(--cyan)" },
                                { label: "Transactions", value: totalTransactions, color: "var(--green)" },
                                { label: "Avg Basket", value: `ETB ${avgBasket.toFixed(2)}`, color: "var(--amber)" },
                            ].map((k) => (
                                <div key={k.label} style={{
                                    background: "var(--surface)", border: "1px solid var(--border)",
                                    borderRadius: "var(--radius)", padding: "14px", textAlign: "center"
                                }}>
                                    <p style={{ margin: 0, fontSize: "11px", color: "var(--text3)", marginBottom: "6px" }}>
                                        {k.label}
                                    </p>
                                    <p style={{
                                        margin: 0, fontSize: "18px", fontWeight: 700,
                                        fontFamily: "var(--mono)", color: k.color
                                    }}>
                                        {k.value}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Payment breakdown */}
                        {Object.keys(paymentBreakdown).length > 0 && (
                            <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
                                {Object.entries(paymentBreakdown).map(([method, amt]) => (
                                    <div key={method} style={{
                                        background: "var(--elevated)", border: "1px solid var(--border)",
                                        borderRadius: "var(--radius)", padding: "8px 14px",
                                        fontSize: "12px", color: "var(--text2)"
                                    }}>
                                        <span style={{ textTransform: "capitalize" }}>{method}</span>
                                        <span style={{
                                            marginLeft: "8px", fontFamily: "var(--mono)",
                                            color: "var(--text)", fontWeight: 600
                                        }}>
                                            ETB {Number(amt).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Transaction rows */}
                        {transactions.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px", color: "var(--text3)" }}>
                                <p style={{ fontSize: "32px", marginBottom: "8px" }}>🧾</p>
                                <p>No transactions yet today.</p>
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                {transactions.map((tx) => (
                                    <div key={tx.id} style={{
                                        background: "var(--surface)", border: "1px solid var(--border)",
                                        borderRadius: "var(--radius)", overflow: "hidden"
                                    }}>
                                        {/* Row header — click to expand */}
                                        <div
                                            style={{
                                                display: "flex", justifyContent: "space-between",
                                                alignItems: "center", padding: "12px 14px",
                                                cursor: "pointer", userSelect: "none"
                                            }}
                                            onClick={() => setExpanded(expanded === tx.id ? null : tx.id)}
                                        >
                                            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                                <span style={{
                                                    fontSize: "11px", color: "var(--text3)",
                                                    fontFamily: "var(--mono)"
                                                }}>
                                                    {tx.receipt_number}
                                                </span>
                                                <span style={{ fontSize: "11px", color: "var(--text3)" }}>
                                                    {new Date(tx.created_at).toLocaleTimeString([], {
                                                        hour: "2-digit", minute: "2-digit"
                                                    })}
                                                </span>
                                                <span style={{
                                                    fontSize: "10px", padding: "2px 8px", borderRadius: "20px",
                                                    textTransform: "capitalize",
                                                    background: "var(--cyan-dim)", color: "var(--cyan)"
                                                }}>
                                                    {tx.payment_method}
                                                </span>
                                            </div>
                                            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                                <span style={{
                                                    fontFamily: "var(--mono)", fontWeight: 700, color: "var(--cyan)"
                                                }}>
                                                    ETB {Number(tx.total_amount).toFixed(2)}
                                                </span>
                                                <span style={{ color: "var(--text3)", fontSize: "12px" }}>
                                                    {expanded === tx.id ? "▲" : "▼"}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Expanded detail */}
                                        {expanded === tx.id && (
                                            <div style={{
                                                borderTop: "1px solid var(--border)",
                                                background: "var(--elevated)", padding: "12px 14px"
                                            }}>
                                                <table style={{
                                                    width: "100%", borderCollapse: "collapse",
                                                    fontSize: "12px", marginBottom: "10px"
                                                }}>
                                                    <thead>
                                                        <tr style={{ color: "var(--text3)" }}>
                                                            <th style={{ textAlign: "left", paddingBottom: "6px", fontWeight: 500 }}>Product</th>
                                                            <th style={{ textAlign: "right", paddingBottom: "6px", fontWeight: 500 }}>Qty</th>
                                                            <th style={{ textAlign: "right", paddingBottom: "6px", fontWeight: 500 }}>Unit</th>
                                                            <th style={{ textAlign: "right", paddingBottom: "6px", fontWeight: 500 }}>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(tx.sale_items ?? []).map((item, i) => (
                                                            <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                                                                <td style={{ padding: "5px 0", color: "var(--text)" }}>
                                                                    {item.product_name}
                                                                </td>
                                                                <td style={{
                                                                    padding: "5px 0", textAlign: "right",
                                                                    color: "var(--text2)", fontFamily: "var(--mono)"
                                                                }}>
                                                                    {item.quantity}
                                                                </td>
                                                                <td style={{
                                                                    padding: "5px 0", textAlign: "right",
                                                                    color: "var(--text2)", fontFamily: "var(--mono)"
                                                                }}>
                                                                    ETB {Number(item.unit_price).toFixed(2)}
                                                                </td>
                                                                <td style={{
                                                                    padding: "5px 0", textAlign: "right",
                                                                    color: "var(--text)", fontFamily: "var(--mono)"
                                                                }}>
                                                                    ETB {Number(item.line_total).toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>

                                                {/* Totals */}
                                                <div style={{
                                                    display: "flex", justifyContent: "space-between",
                                                    fontSize: "12px", color: "var(--text2)", marginBottom: "4px"
                                                }}>
                                                    <span>Subtotal</span>
                                                    <span style={{ fontFamily: "var(--mono)" }}>
                                                        ETB {Number(tx.subtotal).toFixed(2)}
                                                    </span>
                                                </div>
                                                {vatEnabled && tx.tax_amount > 0 && (
                                                    <div style={{
                                                        display: "flex", justifyContent: "space-between",
                                                        fontSize: "12px", color: "var(--amber)", marginBottom: "4px"
                                                    }}>
                                                        <span>VAT</span>
                                                        <span style={{ fontFamily: "var(--mono)" }}>
                                                            ETB {Number(tx.tax_amount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div style={{
                                                    display: "flex", justifyContent: "space-between",
                                                    fontSize: "13px", fontWeight: 700,
                                                    color: "var(--cyan)", marginBottom: "10px"
                                                }}>
                                                    <span>Total</span>
                                                    <span style={{ fontFamily: "var(--mono)" }}>
                                                        ETB {Number(tx.total_amount).toFixed(2)}
                                                    </span>
                                                </div>

                                                <button
                                                    onClick={() => handlePrintReceipt(tx)}
                                                    style={{
                                                        width: "100%", padding: "8px",
                                                        borderRadius: "var(--radius)",
                                                        background: "var(--cyan-dim)", color: "var(--cyan)",
                                                        border: "1px solid rgba(50,212,224,0.25)",
                                                        cursor: "pointer", fontSize: "12px", fontWeight: 600,
                                                        fontFamily: "var(--font)"
                                                    }}
                                                >
                                                    🖨 Print Receipt
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}